import ConfirmationDialog from '@rio-cloud/rio-uikit/ConfirmationDialog';
import Dialog from '@rio-cloud/rio-uikit/Dialog';
import Select, { SelectOption } from '@rio-cloud/rio-uikit/Select';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { usePutCompoundAccessMutation } from '../api/serviceProviderApi';
import { handleQueryError } from '../notifications/ErrorNotification';
import { showSuccessNotification } from '../notifications/SuccessNotification';
import { CompoundAccess, ServiceProvider } from './serviceProvider.types';

const useCompoundAccessLabel = (accessesLevel: CompoundAccess) => {
    const intl = useIntl();

    switch (accessesLevel) {
        case CompoundAccess.NONE:
            return intl.formatMessage({ id: 'outboundOrderBook.serviceProviders.compoundAccess.none' });
        case CompoundAccess.READ_ONLY:
            return intl.formatMessage({ id: 'outboundOrderBook.serviceProviders.compoundAccess.readOnly' });
        case CompoundAccess.READ_WRITE:
            return intl.formatMessage({ id: 'outboundOrderBook.serviceProviders.compoundAccess.readWrite' });
    }
};

export const CompoundAccessSelect = ({ serviceProvider }: { serviceProvider: ServiceProvider }) => {
    const [selectedAccess, setSelectedAccess] = useState(serviceProvider.compoundAccess);
    const [requestedAccessChange, setRequestedAccessChange] = useState<CompoundAccess>(serviceProvider.compoundAccess);
    const [putCompoundAccess, compoundAccessRequest] = usePutCompoundAccessMutation();
    const inputId = `compound-access-${serviceProvider.accountId}`;
    // Necessary to keep the previous selection when the request dialog was aborted.
    // By changing the key we enforce a rerender to reset the selects internal state
    const selectKey = `${selectedAccess}-${requestedAccessChange ?? 'confirmed'}`;

    const options = [
        {
            id: CompoundAccess.NONE,
            label: useCompoundAccessLabel(CompoundAccess.NONE),
            icon: <span className="rioglyph rioglyph-ban-circle text-color-rating-1" />,
        },
        {
            id: CompoundAccess.READ_ONLY,
            label: useCompoundAccessLabel(CompoundAccess.READ_ONLY),
            icon: <span className="rioglyph rioglyph-eye-open text-color-rating-2" />,
        },
        {
            id: CompoundAccess.READ_WRITE,
            label: useCompoundAccessLabel(CompoundAccess.READ_WRITE),
            icon: <span className="rioglyph rioglyph-checkbox-checked text-color-rating-4" />,
        },
    ];

    const updateCompoundAccess = async (compoundAccess: CompoundAccess) => {
        setSelectedAccess(compoundAccess);
        try {
            await putCompoundAccess({ accountId: serviceProvider.accountId, compoundAccess }).unwrap();
            showSuccessNotification('outboundOrderBook.serviceProviders.compoundAccess.success');
        } catch (error: unknown) {
            setSelectedAccess(serviceProvider.compoundAccess);
            handleQueryError(error);
        }
    };

    const changeCompoundAccess = (option: SelectOption | undefined) => {
        const nextAccess = option?.id as CompoundAccess | undefined;
        if (nextAccess === undefined || nextAccess === selectedAccess) {
            return;
        }
        setRequestedAccessChange(nextAccess);
    };

    const confirmDowngrade = () => {
        if (requestedAccessChange !== undefined) {
            void updateCompoundAccess(requestedAccessChange);
        }
        setRequestedAccessChange(serviceProvider.compoundAccess);
    };

    return (
        <>
            <Select
                key={selectKey}
                id={inputId}
                disabled={compoundAccessRequest.isLoading}
                options={options}
                value={[selectedAccess]}
                onChange={changeCompoundAccess}
            />
            <ConfirmationDialog
                show={requestedAccessChange !== serviceProvider.compoundAccess}
                title={<FormattedMessage id="outboundOrderBook.serviceProviders.compoundAccess.confirmation.title" />}
                content={
                    <FormattedMessage
                        id="outboundOrderBook.serviceProviders.compoundAccess.confirmation.text"
                        values={{
                            serviceProviderName: <strong>{serviceProvider.serviceProviderName}</strong>,
                            from: <strong>{useCompoundAccessLabel(serviceProvider.compoundAccess)}</strong>,
                            to: <strong>{useCompoundAccessLabel(requestedAccessChange)}</strong>,
                        }}
                    />
                }
                bsSize={Dialog.SIZE_SM}
                onClickConfirm={confirmDowngrade}
                onClickCancel={() => setRequestedAccessChange(serviceProvider.compoundAccess)}
                cancelButtonText={<FormattedMessage id="outboundOrderBook.common.button.cancel" />}
                confirmButtonText={<FormattedMessage id="outboundOrderBook.common.button.apply" />}
            />
        </>
    );
};
